import type { Allocation } from "../../../api/SolarCloudApi/models/Allocation";
import type { Order } from "../../../api/SolarCloudApi/models/Order";
import type { TrackerSummary } from "../../../queries/tracker/useTrackerSummary";

const excludedPurchaseStatuses = new Set([
  "draft",
  "canceled",
  "cancelled",
  "cancelpending",
]);

export function getDashboardPurchases(
  orders: Order[] | undefined
): Order[] | undefined {
  if (orders === undefined) return undefined;

  return orders
    .map((order, index) => ({
      order,
      index,
      createdAt: order.created ? Date.parse(order.created) : Number.NaN,
    }))
    .filter(({ order }) => {
      const status = order.status?.toLowerCase().trim();
      return !status || !excludedPurchaseStatuses.has(status);
    })
    .sort((first, second) => {
      const firstDateIsValid = Number.isFinite(first.createdAt);
      const secondDateIsValid = Number.isFinite(second.createdAt);

      if (!firstDateIsValid && !secondDateIsValid) {
        return first.index - second.index;
      }
      if (!firstDateIsValid) return 1;
      if (!secondDateIsValid) return -1;

      return second.createdAt - first.createdAt || first.index - second.index;
    })
    .map(({ order }) => order);
}

export function getKilowattsFromWatts(
  wattCapacity: number | null | undefined
): number | undefined {
  if (wattCapacity == null || !Number.isFinite(wattCapacity)) return undefined;
  return Number((wattCapacity / 1000).toFixed(1));
}

export function getPanelsFromWatts(
  wattCapacity: number | null | undefined
): number | undefined {
  if (wattCapacity == null || !Number.isFinite(wattCapacity)) return undefined;
  return Math.round(wattCapacity / 100);
}

export function getOrderPurchaseAmount(order: Order): number | undefined {
  return order.totalPrice ?? order.fullPrice;
}

export type OwnershipStatus =
  | "active"
  | "inProgress"
  | "awaitingActivation"
  | "pending";

export function getOwnershipSummary(
  activeWatts: number | null | undefined,
  orders: Order[] | undefined,
  trackerSummary: TrackerSummary | undefined,
  pendingCapacityWatts?: number
) {
  const purchases = getDashboardPurchases(orders);
  const trackerHasPanels = trackerSummary
    ? Object.values(trackerSummary).reduce(
        (total, milestone) => total + milestone.panelCount,
        0
      ) > 0
    : false;
  const hasEligiblePurchases = (purchases?.length ?? 0) > 0;
  const hasActiveGeneration =
    activeWatts !== null &&
    activeWatts !== undefined &&
    Number.isFinite(activeWatts) &&
    activeWatts > 0;
  const hasSetupProgress =
    trackerHasPanels ||
    (pendingCapacityWatts !== undefined && pendingCapacityWatts > 0);
  const hasKnownPendingState =
    activeWatts === 0 && purchases !== undefined && trackerSummary !== undefined;
  const status: OwnershipStatus | undefined = hasActiveGeneration
    ? "active"
    : hasSetupProgress
      ? "inProgress"
      : hasEligiblePurchases
        ? "awaitingActivation"
        : hasKnownPendingState
          ? "pending"
          : undefined;

  return {
    systemSizeKw: getKilowattsFromWatts(activeWatts),
    panelCount: getPanelsFromWatts(activeWatts),
    purchaseCount: purchases?.length,
    pendingCapacityKw: getKilowattsFromWatts(pendingCapacityWatts),
    status,
  };
}

export function getLinkedElectricityAllocations(
  allocations: Allocation[] | undefined
): Allocation[] {
  return (allocations ?? []).filter(
    (allocation) =>
      !allocation.bankPayee &&
      !allocation.nickname?.startsWith("::gas::") &&
      allocation.energyCompanyPayee != null
  );
}

export function maskCustomerReferenceNumber(
  referenceNumber: string | null | undefined
): string | undefined {
  const normalizedReference = referenceNumber?.replace(/\s/g, "");
  if (!normalizedReference) return undefined;

  if (normalizedReference.length <= 4) {
    return "•".repeat(normalizedReference.length);
  }

  return `${"•".repeat(normalizedReference.length - 4)}${normalizedReference.slice(-4)}`;
}
